import React, { useState } from 'react';
import { HardDrive, HelpCircle } from 'lucide-react';
import LiquidGauge from './LiquidGauge';
import { formatCapacity } from '@/lib/utils/format';

interface CapacityGaugeProps {
  used: number;
  total: number; // Raw capacity
  usable?: number; // Usable capacity (after RAID, HA, spares)
  clusters?: any[];
}

const CapacityGauge: React.FC<CapacityGaugeProps> = ({ used, total, usable, clusters = [] }) => {
  // Ensure used and total are numbers
  const usedNum = Number(used) || 0;
  const totalNum = Number(total) || 1;
  const usableNum = Number(usable) || 0;

  // IMPORTANT: Percentage should be calculated against USABLE capacity, not raw
  // If usable is provided, use it; otherwise fall back to total (raw) for backward compatibility
  const hasUsable = usableNum > 0;
  // The headline Total must be the same capacity the percentage divides by,
  // otherwise the card reads "4 PiB of 11 PiB - 50.1%", which cannot be true.
  const capacityForPercentage = hasUsable ? usableNum : totalNum;
  const usagePercent = capacityForPercentage > 0 ? (usedNum / capacityForPercentage) * 100 : 0;

  // Calculate efficiency ratio (usable / raw)
  const efficiencyRatio = usableNum > 0 && totalNum > 0 ? (usableNum / totalNum) * 100 : 50;

  // State for tooltip visibility and position
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const icon = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 320; // w-80 = 320px
    const tooltipHeight = 240; // approximate height
    const offset = 16; // spacing from icon

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate initial position (left of icon)
    let x = icon.left - tooltipWidth - offset;
    let y = icon.top + icon.height / 2 - tooltipHeight / 2;

    // Check if tooltip would go off the left edge
    if (x < 10) {
      // Position to the right of icon instead
      x = icon.right + offset;
    }

    // Check if tooltip would go off the right edge
    if (x + tooltipWidth > viewportWidth - 10) {
      // Position to the left of icon
      x = icon.left - tooltipWidth - offset;
    }

    // Check if tooltip would go off the top edge
    if (y < 10) {
      y = 10;
    }

    // Check if tooltip would go off the bottom edge
    if (y + tooltipHeight > viewportHeight - 10) {
      y = viewportHeight - tooltipHeight - 10;
    }

    setTooltipPosition({ x, y });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-full min-h-[400px] flex flex-col">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Usage Metrics
      </h3>

      {/* Content wrapper for proper spacing */}
      <div className="flex-1 flex flex-col justify-center">
        {/* Liquid Gauge Chart */}
        <div className="flex justify-center mb-6">
          <LiquidGauge value={usagePercent} size={160} />
        </div>

        {/* Storage Summary */}
        <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div data-testid="capacity-used" className="text-2xl font-semibold text-gray-900 dark:text-white">
            {formatCapacity(usedNum, 2)}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Used</div>
        </div>
        <div className="text-center">
          <div data-testid="capacity-total" className="text-2xl font-semibold text-gray-900 dark:text-white">
            {formatCapacity(capacityForPercentage, 2)}
          </div>
          <div className="flex items-center justify-center gap-1 text-sm text-gray-600 dark:text-gray-400">
            <span data-testid="capacity-total-label">{hasUsable ? 'Usable' : 'Raw'}</span>
            <div className="relative inline-block">
              <HelpCircle
                className="w-4 h-4 cursor-help text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
              {showTooltip && (
                <div
                  className="fixed w-80 z-[9999] pointer-events-none"
                  style={{
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                  }}
                >
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-600 animate-in fade-in duration-200">
                    <div className="font-semibold mb-2">Storage Capacity Calculation</div>
                    <div className="space-y-1 text-gray-200">
                      <div className="bg-gray-800 dark:bg-gray-600 rounded p-2 mb-2">
                        <div className="text-sm font-bold text-blue-400 mb-1">
                          Raw Capacity: {formatCapacity(totalNum, 2)}
                        </div>
                        <div className="text-xs text-gray-300">
                          ({totalNum.toFixed(2)} TiB total physical disk space)
                        </div>
                      </div>
                      {usableNum > 0 && (
                        <>
                          <div className="flex items-center justify-between text-xs my-2">
                            <span className="text-gray-400">After RAID, HA, spares:</span>
                            <span className="font-semibold">↓ {efficiencyRatio.toFixed(1)}%</span>
                          </div>
                          <div className="bg-green-900/30 dark:bg-green-800/30 rounded p-2 mb-2 border border-green-700">
                            <div className="text-sm font-bold text-green-400 mb-1">
                              Usable Capacity: {formatCapacity(usableNum, 2)}
                            </div>
                            <div className="text-xs text-gray-300">
                              ({usableNum.toFixed(2)} TiB available for data storage)
                            </div>
                          </div>
                        </>
                      )}
                      <div className="text-[10px] text-gray-300 mt-2">
                        <strong>Capacity reduction factors:</strong>
                      </div>
                      <ul className="list-disc list-inside ml-2 space-y-1 text-[10px]">
                        <li><strong>RAID-DP:</strong> Double-parity protection (~20-30% overhead)</li>
                        <li><strong>HA mirroring:</strong> Node redundancy (~5-10% overhead)</li>
                        <li><strong>Hot spares:</strong> Reserved disks (~5% overhead)</li>
                        <li><strong>Aggregate metadata:</strong> Filesystem overhead (~2-5%)</li>
                      </ul>
                      <div className="mt-2 pt-2 border-t border-gray-600 text-[10px]">
                        <div className="text-gray-300">
                          Typical efficiency: 45-55% is normal for enterprise NetApp systems with high availability
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

        {/* Raw capacity, kept separate so it is never mistaken for the
            denominator behind the usage percentage. */}
        {hasUsable && (
          <div
            data-testid="capacity-raw"
            className="text-center text-xs text-gray-500 dark:text-gray-400 mb-4"
          >
            {formatCapacity(totalNum, 2)} raw &middot; {Math.round(efficiencyRatio)}% usable
            after RAID, HA and spares
          </div>
        )}

        {/* Top Clusters */}
        {clusters.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Top Clusters by Capacity
          </h4>
          <div className="space-y-2">
            {clusters.slice(0, 3).map((cluster, idx) => (
              <div key={cluster.id || idx} className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <HardDrive className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {cluster.name}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white ml-2">
                  {formatCapacity(cluster.capacity_tb)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CapacityGauge;